// Take a snapshot of one database: which parts it holds (every part ticked to
// begin with), what the file is called, and where it is saved. Opened from the
// databases list on Admin and from the Backup tab on Data Management.
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { fetchSnapshot, fetchSnapshotContents, type SnapshotContents } from '@/lib/api';
import {
  canChooseLocation,
  canRememberFolder,
  defaultSnapshotFolder,
  prepareDestination,
  type SaveTarget,
} from '@/lib/saveFile';
import { Modal } from '@/components/ui/Modal';
import { Message } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';

interface Part {
  key: string;
  label: string;
  count: string;
  /** The parts this one's rows refer to; it is taken only with them. */
  parents: string[];
}

const n = (x: number) => x.toLocaleString();

/** The parts a snapshot of this database can hold, with what each holds now. */
function snapshotParts(c: SnapshotContents): Part[] {
  return [
    { key: 'graphs', label: 'Graphs', count: `${n(c.graphs)} graphs, ${n(c.statements)} statements`, parents: [] },
    { key: 'prefixes', label: 'Prefixes', count: n(c.prefixes), parents: [] },
    { key: 'rules', label: 'Rules', count: `${n(c.rules)}, ${n(c.rules_off)} switched off`, parents: [] },
    { key: 'corrections', label: 'Corrections', count: n(c.corrections), parents: [] },
    ...c.state.map((s) => ({
      key: s.table,
      label: s.label.charAt(0).toUpperCase() + s.label.slice(1),
      count: n(s.rows),
      parents: s.parents ?? [],
    })),
  ];
}

export function SnapshotModal({
  databaseId,
  databaseName,
  onClose,
}: {
  databaseId: string;
  databaseName: string;
  onClose: () => void;
}) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const contents = useQuery({
    queryKey: ['snapshot-contents', databaseId],
    queryFn: () => fetchSnapshotContents(token ?? '', databaseId),
    enabled: !!token,
  });
  const day = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState(`${databaseName}-snapshot-${day}`);
  const [left, setLeft] = useState<Set<string>>(new Set());
  const [folder, setFolder] = useState<FileSystemDirectoryHandle | null>(null);
  const [target, setTarget] = useState<SaveTarget>(canChooseLocation() ? 'choose' : 'downloads');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void defaultSnapshotFolder().then((f) => {
      if (live && f) {
        setFolder(f);
        setTarget('default');
      }
    });
    return () => {
      live = false;
    };
  }, []);

  const parts = contents.data?.ok ? snapshotParts(contents.data.data) : [];
  const taken = parts.filter((p) => !left.has(p.key));
  // A part is taken only with the parts it refers to: ticking questions ticks
  // their banks, and leaving out the banks leaves out what refers to them.
  const toggle = (key: string) =>
    setLeft((prev) => {
      const next = new Set(prev);
      const spread = (start: string, step: (k: string) => string[]) => {
        const todo = [start];
        while (todo.length) {
          const k = todo.pop() as string;
          for (const m of step(k)) {
            if (prev.has(key) ? next.has(m) : !next.has(m)) {
              if (prev.has(key)) next.delete(m);
              else next.add(m);
              todo.push(m);
            }
          }
        }
      };
      if (prev.has(key)) {
        next.delete(key);
        spread(key, (k) => parts.find((p) => p.key === k)?.parents ?? []);
      } else {
        next.add(key);
        spread(key, (k) => parts.filter((p) => p.parents.includes(k)).map((p) => p.key));
      }
      return next;
    });

  async function take() {
    if (!token) return;
    setError(null);
    setOutcome(null);
    if (taken.length === 0) {
      setError('Tick at least one part to take a snapshot.');
      return;
    }
    const stem = name.trim() || `${databaseName}-snapshot-${day}`;
    const destination = await prepareDestination(target, `${stem}.zip`, folder);
    if (!destination) {
      setError(
        target === 'default'
          ? `Not saved: this browser was not given leave to write to the folder ${folder?.name ?? ''}.`
          : 'Not saved: no place to save it was chosen.',
      );
      return;
    }
    setBusy(true);
    // Every part ticked asks for the whole database, so a part added to the
    // backup later is never left out by an old list.
    const r = await fetchSnapshot(token, databaseId, {
      ...(taken.length === parts.length ? {} : { include: taken.map((p) => p.key) }),
      name: stem,
    });
    if (!r.ok) {
      setBusy(false);
      setError(r.message);
      return;
    }
    try {
      await destination.write(r.blob, r.filename);
    } catch (e) {
      setBusy(false);
      setError(`The snapshot was taken but could not be written to ${destination.where}: ${String(e)}`);
      return;
    }
    setBusy(false);
    const notes =
      r.warningCount > 0
        ? ` ${r.warningCount} thing(s) in it could not be written exactly: ${r.warnings.join('; ')}.`
        : '';
    setOutcome(`Saved ${r.filename} to ${destination.where}.${notes}`);
  }

  return (
    <Modal
      title={`Snapshot of ${databaseName}`}
      subtitle="Pick what it holds, name the file and choose where it goes."
      onClose={onClose}
      busy={busy}
      size="md"
      footer={
        <>
          <button type="button" className={buttonVariants({ variant: 'secondary', size: 'md' })} onClick={onClose} disabled={busy}>
            {outcome ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className={buttonVariants({ variant: 'primary', size: 'md' })}
            onClick={() => void take()}
            disabled={busy || !contents.data?.ok}
          >
            {busy ? 'Taking the snapshot…' : 'Take snapshot'}
          </button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <fieldset>
          <legend className="font-medium">What it holds</legend>
          {contents.isLoading && <p className="mt-1 text-tres-text-secondary">Reading what {databaseName} holds…</p>}
          {contents.data && !contents.data.ok && (
            <Message kind="warning" density="compact" className="mt-1">
              {contents.data.message}
            </Message>
          )}
          <ul className="mt-1 space-y-1">
            {parts.map((p) => (
              <li key={p.key}>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!left.has(p.key)} onChange={() => toggle(p.key)} disabled={busy} />
                  <span>{p.label}</span>
                  <span className="text-xs text-tres-text-secondary">{p.count}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        <label className="block">
          <span className="font-medium">File name</span>
          <input
            className={fieldClass('normal', 'mt-1 w-full py-1')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            aria-label="File name"
          />
          <span className="mt-0.5 block text-xs text-tres-text-secondary">Saved as {(name.trim() || `${databaseName}-snapshot-${day}`)}.zip</span>
        </label>

        <fieldset>
          <legend className="font-medium">Save to</legend>
          <div className="mt-1 space-y-1">
            {canChooseLocation() && (
              <label className="flex items-center gap-2">
                <input type="radio" name="snapshot-target" checked={target === 'choose'} onChange={() => setTarget('choose')} disabled={busy} />
                Ask where to save it
              </label>
            )}
            {folder && (
              <label className="flex items-center gap-2">
                <input type="radio" name="snapshot-target" checked={target === 'default'} onChange={() => setTarget('default')} disabled={busy} />
                The default folder, {folder.name}
              </label>
            )}
            <label className="flex items-center gap-2">
              <input type="radio" name="snapshot-target" checked={target === 'downloads'} onChange={() => setTarget('downloads')} disabled={busy} />
              Your Downloads folder
            </label>
          </div>
          {!canChooseLocation() && (
            <p className="mt-1 text-xs text-tres-text-secondary">This browser saves every file to its Downloads folder.</p>
          )}
          {canRememberFolder() && !folder && (
            <p className="mt-1 text-xs text-tres-text-secondary">A default folder for snapshots can be set in Preferences.</p>
          )}
        </fieldset>

        {error && (
          <Message kind="warning" density="compact">
            {error}
          </Message>
        )}
        {outcome && (
          <Message kind="outcome" density="compact">
            {outcome}
          </Message>
        )}
      </div>
    </Modal>
  );
}
